import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface HireNowData {
  engineers: number;
  modelLabel: string;
  modelRate: number;
  modelUnit: 'hourly' | 'monthly';
  durationMonths: number;
  hoursPerWeek?: number;
  salary: number;
  thoughticTotal: number;
  inHouseTotal: number;
  savings: number;
  savingsPercent: number;
}

interface ModelRate {
  label: string;
  rate: number;
  unit: 'hourly' | 'monthly';
  minEngineers: number;
  description: string;
  color: string;
  bg: string;
  border: string;
}

const models: ModelRate[] = [
  {
    label: 'Staff Augmentation',
    rate: 65,
    unit: 'hourly',
    minEngineers: 1,
    description: 'Flexible hourly billing. Scale up or down weekly.',
    color: 'text-primary-600',
    bg: 'bg-primary-50',
    border: 'border-primary-200',
  },
  {
    label: 'Dedicated Team',
    rate: 8000,
    unit: 'monthly',
    minEngineers: 2,
    description: 'Fixed monthly per engineer. Best for long-term builds.',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
  {
    label: 'Contract Hiring',
    rate: 85,
    unit: 'hourly',
    minEngineers: 1,
    description: 'Project-based scope with defined deliverables.',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  {
    label: 'Offshore Center',
    rate: 4500,
    unit: 'monthly',
    minEngineers: 5,
    description: 'Enterprise agreement. Full offshore capability.',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
  },
];

const INHOUSE_LOAD_FACTOR = 1.45;
const INHOUSE_TIME_TO_HIRE_MONTHS = 3.5;
const THOUGHTIC_TIME_TO_START_WEEKS = 0.5;

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

function formatPercent(n: number): string {
  return `${Math.round(n)}%`;
}

export interface ROICalculatorProps {
  onHireNow?: (data: HireNowData) => void;
}

export default function ROICalculator({ onHireNow }: ROICalculatorProps) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [engineers, setEngineers] = useState(3);
  const [modelIndex, setModelIndex] = useState(0);
  const [hoursPerWeek, setHoursPerWeek] = useState(40);
  const [durationMonths, setDurationMonths] = useState(6);
  const [salary, setSalary] = useState(140000);

  const model = models[modelIndex];

  const results = useMemo(() => {
    const totalHours = engineers * hoursPerWeek * 4.33 * durationMonths;

    let thoughticTotal = 0;
    if (model.unit === 'hourly') {
      thoughticTotal = totalHours * model.rate;
    } else {
      thoughticTotal = engineers * model.rate * durationMonths;
    }

    const inHouseAnnual = salary * INHOUSE_LOAD_FACTOR;
    const inHouseMonthly = inHouseAnnual / 12;
    const inHouseTotal = inHouseMonthly * durationMonths * engineers;

    const savings = inHouseTotal - thoughticTotal;
    const savingsPercent = savings > 0 ? (savings / inHouseTotal) * 100 : 0;

    const timeSavedMonths = INHOUSE_TIME_TO_HIRE_MONTHS - THOUGHTIC_TIME_TO_START_WEEKS / 4;
    const timeSavedPercent = (timeSavedMonths / INHOUSE_TIME_TO_HIRE_MONTHS) * 100;

    return {
      thoughticTotal,
      inHouseTotal,
      savings,
      savingsPercent,
      timeSavedMonths,
      timeSavedPercent,
      totalHours,
    };
  }, [engineers, modelIndex, hoursPerWeek, durationMonths, salary]);

  const handleHireNow = () => {
    if (!onHireNow) return;
    onHireNow({
      engineers,
      modelLabel: model.label,
      modelRate: model.rate,
      modelUnit: model.unit,
      durationMonths,
      hoursPerWeek: model.unit === 'hourly' ? hoursPerWeek : undefined,
      salary,
      thoughticTotal: results.thoughticTotal,
      inHouseTotal: results.inHouseTotal,
      savings: results.savings,
      savingsPercent: results.savingsPercent,
    });
  };

  const barWidth = (val: number, max: number) => `${clamp((val / max) * 100, 5, 100)}%`;
  const maxCost = Math.max(results.inHouseTotal, results.thoughticTotal);

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-50/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-50/30 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-calculator-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Interactive Tool</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4"
          >
            Engagement ROI
            <span className="text-primary-600"> Calculator</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Plug in your team size, engagement model, and timeline. See exactly how much you save versus traditional hiring.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
          {/* Inputs panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="lg:col-span-2 bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100"
          >
            <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-6">Your Inputs</h3>

            {/* Engineers */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Engineers Needed</label>
                <span className="text-sm font-bold text-navy-900">{engineers}</span>
              </div>
              <input
                type="range"
                min={1}
                max={50}
                value={engineers}
                onChange={(e) => setEngineers(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-navy-900"
              />
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>1</span>
                <span>50</span>
              </div>
            </div>

            {/* Engagement Model */}
            <div className="mb-6">
              <label className="text-sm font-semibold text-gray-700 mb-3 block">Engagement Model</label>
              <div className="space-y-2">
                {models.map((m, i) => (
                  <button
                    key={m.label}
                    onClick={() => setModelIndex(i)}
                    className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-200 cursor-pointer ${
                      modelIndex === i
                        ? `${m.bg} ${m.border} ${m.color} shadow-sm`
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{m.label}</span>
                      <span className="text-xs font-bold opacity-70">
                        {m.unit === 'hourly' ? `$${m.rate}/hr` : `$${(m.rate / 1000).toFixed(0)}k/mo`}
                      </span>
                    </div>
                    {modelIndex === i && (
                      <motion.p
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="text-xs mt-1 opacity-70 font-normal"
                      >
                        {m.description}
                      </motion.p>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Hours per week */}
            {model.unit === 'hourly' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                className="mb-6"
              >
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">Hours / Week / Engineer</label>
                  <span className="text-sm font-bold text-navy-900">{hoursPerWeek}h</span>
                </div>
                <input
                  type="range"
                  min={10}
                  max={60}
                  step={5}
                  value={hoursPerWeek}
                  onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-navy-900"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>10h</span>
                  <span>60h</span>
                </div>
              </motion.div>
            )}

            {/* Duration */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Project Duration</label>
                <span className="text-sm font-bold text-navy-900">{durationMonths} mo</span>
              </div>
              <input
                type="range"
                min={1}
                max={24}
                value={durationMonths}
                onChange={(e) => setDurationMonths(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-navy-900"
              />
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>1 mo</span>
                <span>24 mo</span>
              </div>
            </div>

            {/* Salary */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Avg In-House Salary</label>
                <span className="text-sm font-bold text-navy-900">${(salary / 1000).toFixed(0)}k</span>
              </div>
              <input
                type="range"
                min={60000}
                max={300000}
                step={5000}
                value={salary}
                onChange={(e) => setSalary(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-navy-900"
              />
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>$60k</span>
                <span>$300k</span>
              </div>
            </div>
          </motion.div>

          {/* Results panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="lg:col-span-3 space-y-6"
          >
            {/* Top metrics grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {[
                { label: 'Your Cost', value: formatCurrency(results.thoughticTotal), sub: 'with Thoughtic', color: 'text-navy-900', bg: 'bg-navy-900', icon: 'ri-money-dollar-circle-line' },
                { label: 'In-House Cost', value: formatCurrency(results.inHouseTotal), sub: 'loaded + overhead', color: 'text-gray-500', bg: 'bg-gray-400', icon: 'ri-bank-line' },
                { label: 'You Save', value: formatCurrency(results.savings), sub: `${formatPercent(results.savingsPercent)} less`, color: 'text-emerald-600', bg: 'bg-emerald-500', icon: 'ri-arrow-down-circle-line' },
                { label: 'Time Saved', value: `${results.timeSavedMonths.toFixed(1)} mo`, sub: 'vs. traditional hiring', color: 'text-primary-600', bg: 'bg-primary-500', icon: 'ri-time-line' },
              ].map((m, i) => (
                <motion.div
                  key={m.label}
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.35 + i * 0.06, duration: 0.4 }}
                  className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${m.bg} mb-3`}>
                    <i className={`${m.icon} text-white text-lg`}></i>
                  </div>
                  <div className="text-xs text-gray-500 font-medium mb-1">{m.label}</div>
                  <div className={`text-xl md:text-2xl font-bold ${m.color} mb-0.5`}>{m.value}</div>
                  <div className="text-[10px] text-gray-400 font-medium">{m.sub}</div>
                </motion.div>
              ))}
            </div>

            {/* Cost comparison bars */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100">
              <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-6">Cost Comparison</h4>

              <div className="space-y-5">
                {/* Thoughtic bar */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-navy-900"></div>
                      <span className="text-sm font-semibold text-navy-900">Thoughtic ({model.label})</span>
                    </div>
                    <span className="text-sm font-bold text-navy-900">{formatCurrency(results.thoughticTotal)}</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={inView ? { width: barWidth(results.thoughticTotal, maxCost) } : {}}
                      transition={{ delay: 0.5, duration: 0.8, ease: 'easeOut' }}
                      className="h-full bg-navy-900 rounded-full"
                    ></motion.div>
                  </div>
                </div>

                {/* In-house bar */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                      <span className="text-sm font-semibold text-gray-600">Traditional In-House Hiring</span>
                    </div>
                    <span className="text-sm font-bold text-gray-600">{formatCurrency(results.inHouseTotal)}</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={inView ? { width: barWidth(results.inHouseTotal, maxCost) } : {}}
                      transition={{ delay: 0.6, duration: 0.8, ease: 'easeOut' }}
                      className="h-full bg-gray-400 rounded-full"
                    ></motion.div>
                  </div>
                </div>

                {/* Savings bar */}
                {results.savings > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                        <span className="text-sm font-semibold text-emerald-600">Your Savings</span>
                      </div>
                      <span className="text-sm font-bold text-emerald-600">{formatCurrency(results.savings)}</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={inView ? { width: barWidth(results.savings, maxCost) } : {}}
                        transition={{ delay: 0.7, duration: 0.8, ease: 'easeOut' }}
                        className="h-full bg-emerald-500 rounded-full"
                      ></motion.div>
                    </div>
                  </div>
                )}
              </div>

              {/* Insight note */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-lightbulb-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Based on a <strong>{formatPercent(INHOUSE_LOAD_FACTOR * 100 - 100)} loaded cost multiplier</strong> for in-house hiring
                      (salary + benefits + overhead + recruiter fees). Thoughtic pricing includes all overhead,
                      and your team starts in <strong>{THOUGHTIC_TIME_TO_START_WEEKS} week</strong> versus{' '}
                      <strong>{INHOUSE_TIME_TO_HIRE_MONTHS} months</strong> of traditional recruiting.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Time comparison */}
            <div className="bg-navy-900 rounded-2xl p-6 md:p-8 text-white">
              <h4 className="text-sm font-bold uppercase tracking-wider mb-6 text-white/80">Time to First Commit</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-2xl text-primary-400"></i>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">{THOUGHTIC_TIME_TO_START_WEEKS} week</div>
                    <div className="text-sm text-white/60">with Thoughtic</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-close-line text-2xl text-gray-400"></i>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-400">{INHOUSE_TIME_TO_HIRE_MONTHS} months</div>
                    <div className="text-sm text-white/60">typical in-house hiring</div>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-white/10">
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-arrow-right-up-line text-emerald-400"></i>
                  <span className="text-emerald-400 font-semibold">{formatPercent(results.timeSavedPercent)} faster</span>
                  <span className="text-white/60">to market with Thoughtic</span>
                </div>
              </div>
            </div>

            {/* Hire Now CTA */}
            {onHireNow && (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl translate-x-8 -translate-y-8"></div>
                <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-lg font-bold mb-1">Ready to move forward?</h4>
                    <p className="text-sm text-white/80">
                      Pre-fill a hire request with your {engineers} engineer{engineers > 1 ? 's' : ''} at {formatCurrency(results.savings)} estimated savings.
                    </p>
                  </div>
                  <button
                    onClick={handleHireNow}
                    className="px-6 py-3.5 bg-white text-primary-600 text-sm font-semibold rounded-xl hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
                  >
                    <i className="ri-send-plane-line"></i>
                    Hire Now
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}